/**
 * safeBack — go back in history if there's a previous entry, otherwise navigate
 * to a sensible fallback. Prevents about:blank when the page was opened directly.
 *
 * Usage:
 *   import { useSafeBack } from "@/utils/safeBack";
 *   const safeBack = useSafeBack();
 *   <button onClick={() => safeBack("/")}>Back</button>
 */
import { useNavigate } from "react-router-dom";

const PATH_FALLBACKS = {
  "/terms": "/",
  "/privacy": "/",
  "/vendor/apply": "/",
  "/vendor-onboarding": "/vendor/apply",
  "/vendors/apply": "/",
  "/contractor/apply": "/",
  "/vip-signup": "/",
  "/vip": "/",
  "/membership": "/",
  "/contact": "/",
  "/support": "/",
  "/auth/login": "/",
  "/login": "/",
  "/waitlist": "/",
  "/faq": "/",
  "/blog": "/",
  "/about": "/",
  "/pricing": "/",
};

export const resolveFallback = (currentPath) => {
  if (!currentPath) return "/";
  if (PATH_FALLBACKS[currentPath]) return PATH_FALLBACKS[currentPath];
  for (const [prefix, fallback] of Object.entries(PATH_FALLBACKS)) {
    if (currentPath.startsWith(prefix + "/")) return fallback;
  }
  return "/";
};

export const useSafeBack = () => {
  const navigate = useNavigate();
  return (explicitFallback) => {
    const fallback = explicitFallback || resolveFallback(window.location.pathname);
    // history.length is 1 for direct loads/new tabs
    if (window.history.length > 1) {
      // Detect "no real previous entry" — referrer empty + length===2 happens with some browsers
      if (window.history.length === 2 && !document.referrer) {
        navigate(fallback, { replace: true });
        return;
      }
      navigate(-1);
      return;
    }
    navigate(fallback, { replace: true });
  };
};
